import { useVabsAccountsStore } from '@/entities/currency-account';
import { IPaymentTemplate } from '@/entities/payment/lib/serialize';
import { useCountriesStore } from '@/entities/static-data/model';

import {
  VabsParticipantType,
  VabsPaymentClientTypeEnum,
  VabsPaymentSourceOfFunds,
  VabsPaymentSystem,
} from '@/shared/api';

import { SendMoneyFormData } from '../../../../model/schema_new';
import {
  FIELD_NAMES,
  ICreateInnerPayload,
  ICreatePaymentPayload,
} from '../config';

export const serializeFormToInput = (
  item: SendMoneyFormData,
): ICreatePaymentPayload => {
  const isPerson = item.type === VabsParticipantType.B2C; //send money to someone by bank requisites
  return {
    paymentSystem: item.paymentSystem,
    input: {
      attachments: [],
      external: {
        accountHolderName: !isPerson
          ? item.companyName
          : `${item.firstName} ${item.lastName}`,
        accountNumber: item.accountNumber,
        address: {
          countryId: item.recipientAddress?.country?.id || '',
          line1: item.recipientAddress?.city,
          line2: item.recipientAddress?.street,
          postalCode: item.recipientAddress?.postcode,
        },
        bank: {
          addressLine1: item.address?.street,
          postalCode: item.address?.postcode,
          cityName: item.address?.city,
          countryId: item.address?.country?.id || '',
          name: item.name,
        },
        bankCode: item.bankCode,
        clientType: !isPerson
          ? VabsPaymentClientTypeEnum.Corporate
          : VabsPaymentClientTypeEnum.Individual,
        firstName: isPerson ? item.firstName : null,
        intermediaryBank: item.intermediaryBank
          ? {
              accountNumber: item.intermediaryBank.accountNumber,
              addressLine1: item.intermediaryBank.bankInfo.address?.street,
              addressLine2: item.intermediaryBank.bankInfo.address?.postcode,
              bankCode: item.intermediaryBank.swift,
              cityName: item.intermediaryBank.bankInfo.address?.city,
              countryId: item.intermediaryBank.bankInfo.address?.country?.id,
              name: item.intermediaryBank.bankInfo.name,
            }
          : null,
        lastName: isPerson ? item.lastName : null,
      },
      internal: { accountId: item.account.virtualId },
      // TODO add mock for fee
      purposeOfPayment: item.purposeOfPayment ?? '1',
      sourceCurrencyId: item.account.currencyId,
      sourceOfFunds:
        item.sourceOfFunds ?? VabsPaymentSourceOfFunds.CommissionOfSales,
      targetAmount: item.amount,
      targetCurrencyId: item.account.currencyId, //TODO: change when there's convert
    },
  };
};

export const serializeInnerFormToInput = (
  item: SendMoneyFormData,
): ICreateInnerPayload => {
  return {
    paymentSystem: VabsPaymentSystem.Inner,
    input: {
      attachments: [],
      note: item.purposeOfPayment,
      recipientAccountId: item.innerAccountId || '',
      recipientClientId: item.clientId || '',
      recipientEmail: item.innerEmail,
      recipientPhoneNumber: item.innerPhone,
      senderAccountId: item.account.virtualId,
      sourceCurrencyId: item.account.currencyId,
      targetAmount: item.amount,
      targetCurrencyId: item.account.currencyId, //TODO: change when there's convert
    },
  };
};

export const useSerializePaymentToForm = () => {
  //OUTGOING BANK PAYMENTS ONLY
  const accounts = useVabsAccountsStore((state) => state.accounts);
  const countries = useCountriesStore((state) => state.data);
  const serialize = (item: IPaymentTemplate): SendMoneyFormData => {
    const account = accounts.find((i) => i.currencyId === item.currencyId);
    if (item.paymentSystem === VabsPaymentSystem.Inner) {
      return {
        account,
        isFav: item.isFavorite,
        clientId: item.clientId,
        innerAccountId: item.innerAccountId,
        innerEmail: item.email,
        innerPhone: item.phoneNumber,
        isInner: true,
        amount: item.outAmount,
        totalAmount: item.outTotalAmount,
        fee: item.fee,
        companyName: item.name,
        purposeOfPayment: item.purposeOfPayment,
      };
    }
    const bankCountry = countries.find((i) => i?.id === item.country);
    const recCountry = countries.find(
      (i) => i?.id === item.recipientAddress.country,
    );
    const intCountry = countries.find(
      (i) => i?.id === item.intermediaryBank?.bankInfo?.country,
    );
    const option = { id: bankCountry?.id, name: bankCountry?.item?.name };
    const recOption = { id: recCountry?.id, name: recCountry?.item?.name };
    const intOption = { id: intCountry?.id, name: intCountry?.item?.name };
    return {
      account,
      type: item.type,
      isFav: item.isFavorite,
      paymentSystem: item.paymentSystem,
      accountNumber: item.accountNumber,
      bankCode: item.bankCode,
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      companyName: item.companyName || '',
      intermediaryBank: item.intermediaryBank?.swift
        ? {
            accountNumber: item.intermediaryBank?.accountNumber,
            swift: item.intermediaryBank?.swift || '',
            bankInfo: item.intermediaryBank?.bankInfo?.name
              ? {
                  name: item.intermediaryBank?.bankInfo?.name,
                  address: {
                    country: intOption,
                    city: item.intermediaryBank?.bankInfo?.city,
                    street: item.intermediaryBank?.bankInfo?.street,
                    postcode: item.intermediaryBank?.bankInfo?.postcode,
                  },
                }
              : undefined,
          }
        : undefined,
      purposeOfPayment: item.purposeOfPayment,
      sourceOfFunds: item.sourceOfFunds,
      amount: item.outAmount,
      name: item.name,
      address: {
        country: option,
        city: item.address.city,
        street: item.address.street,
        postcode: item.address.postcode,
      },
      email: item.email,
      recipientAddress: {
        country: recOption,
        city: item.recipientAddress.city,
        street: item.recipientAddress.street,
        postcode: item.recipientAddress.postcode,
      },
    };
  };
  return { serialize };
};

export const getFieldName = (
  system?: VabsPaymentSystem | 'ACH' | 'Wire',
  field: 'accountNumber' | 'bankCode',
) => {
  if (!system)
    return field === 'accountNumber' ? 'Account number' : 'Bank code';
  return FIELD_NAMES[system][field]; //TODO: add actual payment systems
};
